/**
 * One inbox row: the group icon, the alert's title and body, the relative
 * time, and the unread treatment that clears when the item is read. The
 * dot column is drawn on every row, filled only when unread, so read and
 * unread rows start their icon at the same x (ISSUE-1185).
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  ChatDotsIcon,
  PersonPlusIcon,
  ScoreboardIcon,
  TrophyIcon,
} from "../../shell/SectionIcon";
import { formatRelativeTime } from "./inboxTime";

export type typeNotificationRowItem = {
  inboxItemId: string;
  group: "invite" | "pools" | "games" | "chat";
  title: string;
  body: string;
  isRead: boolean;
  createdAtIso: string;
};

export type typeNotificationRowProps = {
  item: typeNotificationRowItem;
  now: Date;
  onOpen: (payload: { inboxItemId: string }) => void;
};

const NotificationGroupIcon = ({
  group,
}: {
  group: typeNotificationRowItem["group"];
}): ReactNode => {
  switch (group) {
    case "invite":
      return <PersonPlusIcon className="size-icon-md shrink-0" />;
    case "pools":
      return <TrophyIcon className="size-icon-md shrink-0" />;
    case "games":
      return <ScoreboardIcon className="size-icon-md shrink-0" />;
    case "chat":
      return <ChatDotsIcon className="size-icon-md shrink-0" />;
  }
};

export const NotificationRow = ({
  item,
  now,
  onOpen,
}: typeNotificationRowProps): ReactNode => (
  <button
    type="button"
    className={cn(
      "flex w-full items-start gap-3 rounded-md p-3 text-left",
      !item.isRead && "bg-surface-sunken",
    )}
    onClick={() => {
      onOpen({ inboxItemId: item.inboxItemId });
    }}
  >
    <span
      aria-hidden="true"
      data-testid="notification-row-dot"
      data-state={item.isRead ? "read" : "unread"}
      className={cn(
        "mt-1 size-2 shrink-0 rounded-full",
        !item.isRead && "bg-ink",
      )}
    />
    <span
      aria-hidden="true"
      data-testid="notification-row-icon"
      className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-surface-sunken text-ink-secondary"
    >
      <NotificationGroupIcon group={item.group} />
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      {!item.isRead ? <span className="sr-only">Unread</span> : null}
      <span className="text-body-strong text-ink">{item.title}</span>
      <span className="line-clamp-2 text-body text-ink-secondary">
        {item.body}
      </span>
      <span className="text-caption text-ink-tertiary">
        {formatRelativeTime({ isoInstant: item.createdAtIso, now })}
      </span>
    </span>
  </button>
);
